import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { DocPackTypeService, DocPackTypeFormatStyleElementItem, DocPackTypeFormatStyleGet, GetDocPackTypeFormatStyleParams } from '@app/services/doc-pack-type.service';
import { PdfService } from '@app/services/pdf.service';
import { debounceTime, of, Subject, switchMap } from 'rxjs';

@Component({
    selector: 'tt-doc-pack-type-format',
    templateUrl: './doc-pack-type-format.component.html',
    styleUrls: ['./doc-pack-type-format.component.css'],
})
export class DocPackTypeFormatComponent implements OnChanges {
    @Input()
    public params?: GetDocPackTypeFormatStyleParams;

    public get?: DocPackTypeFormatStyleGet;

    public formatElements: DocPackTypeFormatStyleElementItem[] = [];

    public fontFamilies: Item[] = [];

    public fontStyles: Item[] = [];

    public textAligns: Item[] = [];

    public getChange$ = new Subject<void>();

    public formatElementsChange$ = new Subject<void>();

    public generatedStyle = '';

    public previewPdf?: SafeResourceUrl;

    public files: File[] = [];

    constructor(
        private datatask: DataTaskService,
        private pdf: PdfService,
        private sanitizer: DomSanitizer,
        private docPackType: DocPackTypeService,
    ) {
        this.getChange$.pipe(debounceTime(1000)).subscribe({
            next: async () => {
                if (!!this.get) {
                    this.generatePreview();
                    await this.docPackType.saveDocPackFormatStyle(this.get);
                }
            },
        });
        this.formatElementsChange$.pipe(debounceTime(1000)).subscribe({
            next: async () => {
                if (this.formatElements.length > 0 && this.get) {
                    this.generatePreview();
                    await this.docPackType.saveDocPackFormatStyleElements(this.formatElements);
                    await this.docPackType.saveDocPackFormatStyle(this.get);
                }
            },
        });
    }

    public async generatePreview() {
        if (!this.get) return;

        this.generateStyle();

        const pdf = await this.pdf.convertHTMLToPDF(this.get.generated_style + previewHTML);

        // @ts-ignore
        const doc: MergeDocument = {
            contentType: 'application/pdf',
            content: pdf,
            pagetitle: '',
            level: '1',
            includeTemplate: true,
            index: 0,
        };

        this.previewPdf = this.sanitizer.bypassSecurityTrustResourceUrl(
            await this.pdf.mergePDFs([doc], {
                includeTableOfContents: false,
                template: this.get.background_template,
                pageNumberConfig: {
                    showPageNumber: this.get?.show_pagenumber,
                    showPageNumberOnFirstPage: this.get?.show_pagenumber_firstpage,
                    x: +(this.get?.pagenumber_x ?? 0),
                    y: 0 + (this.get?.pagenumber_y ?? 0),
                    color: this.get?.pagenumber_color as any,
                    fontSize: +(this.get?.pagenumber_fontsize ?? 10),
                },
            }),
        );
    }

    public onFilesChanged() {
        if (!this.get) return;

        const backgroundTemplate = this.files[0];

        if (!!backgroundTemplate) {
            this.get.background_template_filename = backgroundTemplate.name;
            const reader = new FileReader();

            reader.onloadend = () => {
                const base64String = reader.result as string;
                const base64Only = base64String.split(',')[1];
                this.get!.background_template = base64Only;

                this.getChange$.next();
            };

            // Read the file's data
            reader.readAsDataURL(backgroundTemplate);
        } else {
            this.get.background_template_filename = '';
            this.get.background_template = '';
            this.getChange$.next();
        }
    }

    private generateStyle() {
        if (!this.get) return;

        this.get.generated_style = `
        <style>
            @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Open+Sans:ital,wght@0,300..800;1,300..800&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap');            
            
            :root {
                margin-top: ${this.get?.margin_top ?? 0}px;
                margin-right: ${this.get?.margin_right ?? 0}px;
                margin-bottom: ${this.get?.margin_bottom ?? 0}px;
                margin-left: ${this.get?.margin_left ?? 0}px;
            }

            ${this.formatElements
                .map((style) => {
                    return `\n ${style.elementtag} {
                    font-size: ${style.fontsize}px;
                    font-family: ${style.fontfamily};
                    text-align: ${style.textalign ?? 'left'};
                    ${style.fontstyle};
                    color: ${style.textcolor};
                    background-color: ${style.backgroundcolor};
                } \n`;
                })
                .join('\n')}

            ${this.get?.additional_styling}
        </style>
        `;
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.docPackType.getDocPackFormatStyle(this.params);

            try {
                if (!this.get.background_template?.trim() || !this.get.background_template_filename?.trim()) return;

                const binaryString = window.atob(this.get.background_template);

                // 2. Convert the binary string to a Uint8Array
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // 3. Create a Blob from the Uint8Array
                // The Blob object represents immutable raw data.
                const blob = new Blob([bytes], { type: 'application/pdf' });
                this.files = [new File([blob], this.get.background_template_filename)];
            } catch (error) {
                console.error(error);
            }
        }
    }

    private async loadFormatElements() {
        if (!!this.get) {
            this.formatElements = await this.docPackType.getDocPackgetElements(this.get);
        }
    }

    private async loadFontFamilies() {
        this.fontFamilies = await this.getFontFamilies();
    }

    private async loadFontStyles() {
        this.fontStyles = await this.getFontStyles();
    }

    private async loadTextAligns() {
        this.textAligns = await this.getTextAligns();
    }

    private getFontFamilies(): Promise<Item[]> {
        return this.datatask.Post(3964);
    }

    private getFontStyles(): Promise<Item[]> {
        return this.datatask.Post(3965);
    }

    private getTextAligns(): Promise<{ item_id: string; item_name: string }[]> {
        return this.datatask.Post(3360);
    }

    private async init() {
        this.loadFontFamilies();
        this.loadFontStyles();
        this.loadTextAligns();
        await this.loadGet();
        await this.loadFormatElements();
        this.generatePreview();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) this.init();
    }
}

const previewHTML = `
    <h1>Heading 1</h1> <br />
    <h2>Heading 2</h2> <br />
    <h3>Heading 3</h3> <br />
    <h4>Heading 4</h4> <br />
    <h5>Heading 5</h5> <br />
    <h6>Heading 6</h6> <br />
    <p>Paragraf</p> <br />
`;
